import React, { useState, useEffect } from 'react';
import { Box, makeStyles, Typography } from '@material-ui/core';
import { BrokerFavoriteListing, Picture } from 'models';
import {
  SERVICE_OFFERS_PUBLIC_PAGE,
  MINIMUM_AMOUNT_OF_DISPLAYED_LISTINGS,
  MAX_FILTER_PRICE,
} from 'config/constants';
import moment from 'moment';
import { images } from 'assets/images';
import { useIntl } from 'react-intl';
import { getCentrisListingPrice } from 'utils/listing';
import InfiniteScroll from 'react-infinite-scroll-component';
import ListingShowcaseItem from './ListingShowcaseItem';

interface Props {
  listings: BrokerFavoriteListing[];
  searchResults: BrokerFavoriteListing[];
  setImageCarouselOpen: (open: boolean) => void;
  setImageCarouselPictures: (pictures: Picture[]) => void;
  selected_filters: any;
  centris_filters: any;
  centris_listings_from_db: [];
  style?: any;
  is_white_text?: boolean;
}

const useStyles = makeStyles({
  listingCards: {
    display: 'grid',
    flexWrap: 'wrap',
    position: 'relative',
    gridTemplateColumns: 'repeat(auto-fill, 320px)',
    justifyContent: 'space-between',
    width: '100%',
    maxHeight: 1500,
    flexGrow: 1,
    marginTop: 30,
    '@media (max-width: 687px)': {
      justifyContent: 'center',
      maxHeight: 800,
    },
  },
  noListings: {
    marginTop: 20,
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
  },
  imgNoListings: {
    width: 300,
    '@media (max-width: 767px)': {
      width: '70%',
    },
  },
  titleNoListings: {
    marginTop: 20,
    fontSize: 20,
  },
});

function listingsShowcase(props: Props) {
  const {
    listings,
    searchResults,
    setImageCarouselOpen,
    setImageCarouselPictures,
    selected_filters,
    centris_filters,
    centris_listings_from_db,
    style,
    is_white_text,
  } = props;
  const intl = useIntl();
  const [filtered_listings, setFilteredListings] = useState([]);
  const [amount_of_displayed_listings, setAmountOfDisplayedListings] = useState(
    MINIMUM_AMOUNT_OF_DISPLAYED_LISTINGS,
  );
  const [has_more_listings_to_load, setHasMoreListingsToLoad] = useState(true);
  const fmtNoListingsFound = intl.formatMessage({ id: 'portal.noListingsFound' });
  const fmtLoading = intl.formatMessage({ id: 'portal.loading' });
  const styles = useStyles();

  function sortListings(listings: any) {
    return listings.sort((a: any, b: any) => {
      if (a.date_sold && !b.date_sold) {
        return 1;
      }
      if (!a.date_sold && b.date_sold) return -1;
      return moment(a.listing_date).isBefore(b.listing_date) ? 1 : -1;
    });
  }
  function resetInfinitreScroll() {
    setHasMoreListingsToLoad(true);
    setAmountOfDisplayedListings(MINIMUM_AMOUNT_OF_DISPLAYED_LISTINGS);
    const listings_copy = listings.slice(0, MINIMUM_AMOUNT_OF_DISPLAYED_LISTINGS);
    setFilteredListings(sortListings(listings_copy));
  }

  useEffect(() => {
    const filteredListings = listings.filter((listing) => {
      return Object.keys(selected_filters).every((key) => {
        return (
          !selected_filters[key] ||
          listing.service_offers.some((service_offer) => {
            return SERVICE_OFFERS_PUBLIC_PAGE[key].includes(service_offer.id);
          })
        );
      });
    });
    setFilteredListings(sortListings(filteredListings));

    if (filteredListings.length === listings.length) {
      resetInfinitreScroll();
    } else {
      setHasMoreListingsToLoad(false);
    }
  }, [selected_filters]);
  useEffect(() => {
    const listingsToBeFiltered = searchResults.length !== 0 ? searchResults : listings;
    let filteredListings = listingsToBeFiltered.filter((centris_listing: any) => {
      let price_filter =
        getCentrisListingPrice(centris_listing) >= centris_filters.price_range[0] &&
        getCentrisListingPrice(centris_listing) <= centris_filters.price_range[1];
      if (centris_filters.price_range[1] === MAX_FILTER_PRICE) {
        price_filter = getCentrisListingPrice(centris_listing) >= centris_filters.price_range[0];
      }
      let property_type_filter = true;
      if (centris_filters.property_type === 'rent') {
        property_type_filter =
          centris_listing.price === 1 &&
          centris_listing.location_price &&
          centris_listing.rent_price_frequency;
      }
      return (
        centris_listing.bedroom_count >= centris_filters.min_bedrooms &&
        centris_listing.bathroom_count >= centris_filters.min_bathrooms &&
        price_filter &&
        property_type_filter
      );
    });
    filteredListings = sortListings(filteredListings);
    setFilteredListings(filteredListings);
    if (filteredListings.length === listings.length) {
      resetInfinitreScroll();
    } else {
      setHasMoreListingsToLoad(false);
    }
  }, [centris_filters]);

  useEffect(() => {
    if (searchResults.length !== 0) {
      setHasMoreListingsToLoad(false);
    } else {
      resetInfinitreScroll();
    }
    setFilteredListings(sortListings(searchResults));
  }, [searchResults]);
  useEffect(() => {
    const listings_copy = listings.slice(0, MINIMUM_AMOUNT_OF_DISPLAYED_LISTINGS);
    setFilteredListings(sortListings(listings_copy));
  }, [listings]);

  const fetchNextSetOfListings = () => {
    const listings_copy = listings;
    const added_listings = filtered_listings.concat(
      listings_copy.slice(
        amount_of_displayed_listings,
        amount_of_displayed_listings + MINIMUM_AMOUNT_OF_DISPLAYED_LISTINGS,
      ),
    );
    setFilteredListings(sortListings(added_listings));
    const hasMore = filtered_listings.length < listings.length;
    setAmountOfDisplayedListings(
      amount_of_displayed_listings + MINIMUM_AMOUNT_OF_DISPLAYED_LISTINGS,
    );
    setHasMoreListingsToLoad(hasMore);
  };

  return (
    <Box>
      {filtered_listings.length > 0 ? (
        <InfiniteScroll
          dataLength={amount_of_displayed_listings}
          next={() => {
            fetchNextSetOfListings();
          }}
          hasMore={has_more_listings_to_load}
          loader={<Typography variant='h3'>{fmtLoading}</Typography>}
          className={styles.listingCards}
          style={{ overflowX: 'hidden' }}
          scrollThreshold={1}
        >
          {filtered_listings.length > 0 &&
            filtered_listings.map((listing) => {
              const found_centris_listing = centris_listings_from_db.find(
                (centris_listing_from_db: any) => {
                  const mls = listing.mls || listing.listing.mls;
                  return centris_listing_from_db.mls === mls;
                },
              );
              return (
                <ListingShowcaseItem
                  key={listing.id}
                  listing={listing}
                  classes={{
                    cardThumbnailClass: null,
                    buttonTextClass: null,
                    iconClasses: null,
                  }}
                  setImageCarouselOpen={setImageCarouselOpen}
                  setImageCarouselPictures={setImageCarouselPictures}
                  found_centris_listing={found_centris_listing}
                  style={style}
                  is_white_text={is_white_text}
                />
              );
            })}
        </InfiniteScroll>
      ) : null}
      {!filtered_listings.length || !listings.length ? (
        <Box className={styles.noListings}>
          <Typography className={styles.titleNoListings}>{fmtNoListingsFound}</Typography>
          <img
            className={styles.imgNoListings}
            src={images.empty_listing}
            alt='empty listing icon'
          />
        </Box>
      ) : null}
    </Box>
  );
}

export default listingsShowcase;
